import React, { useState, useMemo, useRef, useEffect } from 'react';
import { CompleteKundali, KundaliChartData, Language, PlanetPosition } from '../types/kundali';
import { formatDate } from '../utils/dateUtils';
import { ZODIAC_SIGNS } from '../utils/vedicEngine';
import { getPlanetShortCode, getZodiacName } from '../utils/language';
import { HouseInspectorView } from './HouseInspectorView';
import { UnifiedDashaPanel } from './UnifiedDashaPanel';
import { FocusedTimingDrawer } from './FocusedTimingDrawer';
import { calculatePratyantardashas } from '../utils/dashaCalculator';
import {
  Clock,
  ShieldAlert,
  AlertTriangle,
  ChevronRight,
  Maximize2,
  X,
  Compass,
  CheckCircle2,
  Calendar,
  Layers,
  Activity
} from 'lucide-react';

interface UnifiedPredictiveCockpitProps {
  kundali: CompleteKundali;
  chartData: KundaliChartData;
  selectedHouseNumber: number;
  onSelectHouse: (houseNumber: number) => void;
  selectedPlanet: PlanetPosition | null;
  onSelectPlanet: (planet: PlanetPosition | null) => void;
  lang?: Language;
  layoutMode?: 'wrap' | 'top' | 'dock';
  showFullHouseInspector?: boolean;
  splitPercent?: number;
}

export const UnifiedPredictiveCockpit: React.FC<UnifiedPredictiveCockpitProps> = ({
  kundali,
  chartData,
  selectedHouseNumber,
  onSelectHouse,
  selectedPlanet,
  onSelectPlanet,
  lang = 'en',
  layoutMode = 'dock',
  showFullHouseInspector = true,
  splitPercent
}) => {
  const [isDashaDrawerOpen, setIsDashaDrawerOpen] = useState(false);
  const activeLang = lang;
  const isHi = activeLang === 'hi';

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(600);

  // Dynamic Container Awareness: Adjust internal layouts automatically based on width
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const isNarrow = containerWidth < 460 || (splitPercent !== undefined && splitPercent >= 60);

  const ascSignIdx = chartData.ascendantIndex ?? 0;
  const ascSignName = ZODIAC_SIGNS[ascSignIdx];

  // 1. Maraka & Badhaka Determination
  const marakaDiagnosis = useMemo(() => {
    // 2nd and 7th houses from current chart Lagna
    const sign2Idx = (ascSignIdx + 1) % 12;
    const sign7Idx = (ascSignIdx + 6) % 12;
    const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
    
    const lord2 = ZODIAC_LORDS[sign2Idx];
    const lord7 = ZODIAC_LORDS[sign7Idx];

    // Badhaka House calculation based on Lagna mobility:
    // Chara (Movable: 1, 4, 7, 10 - Aries, Cancer, Libra, Capricorn) -> 11th House
    // Sthira (Fixed: 2, 5, 8, 11 - Taurus, Leo, Scorpio, Aquarius) -> 9th House
    // Dvisvabhava (Dual: 3, 6, 9, 12 - Gemini, Virgo, Sag, Pisces) -> 7th House
    const lagnaNum = ascSignIdx + 1;
    let badhakaHouse = 11;
    if ([2, 5, 8, 11].includes(lagnaNum)) {
      badhakaHouse = 9;
    } else if ([3, 6, 9, 12].includes(lagnaNum)) {
      badhakaHouse = 7;
    }
    const badhakaSignIdx = (ascSignIdx + badhakaHouse - 1) % 12;
    const badhakesh = ZODIAC_LORDS[badhakaSignIdx];

    return {
      lord2,
      lord7,
      badhakaHouse,
      badhakesh,
      marakaPlanets: Array.from(new Set([lord2, lord7]))
    };
  }, [ascSignIdx]);

  // 2. Active Vimshottari Triad Computation
  const currentDashaData = useMemo(() => {
    const md = kundali.dashas.find(d => d.isCurrent) || kundali.dashas[0];
    const ad = md?.antardashas?.find(a => a.isCurrent) || md?.antardashas?.[0];
    let pd = ad?.antardashas?.find(p => p.isCurrent) || ad?.antardashas?.[0];

    // If PD not explicitly present on AD, compute dynamically from Parashari algorithm
    if (!pd && ad?.startDate && ad?.endDate) {
      const pds = calculatePratyantardashas(ad.planet, ad.startDate, ad.endDate);
      const activePd = pds.find(p => p.isCurrent) || pds[0];
      if (activePd) {
        pd = {
          planet: activePd.planet,
          startDate: activePd.startDate,
          endDate: activePd.endDate,
          durationYears: activePd.durationDays / 365.25,
          isCurrent: activePd.isCurrent
        };
      }
    }

    // Days remaining & progress calculation
    let progressPercent = 50;
    let daysRemaining = 0;
    if (ad?.startDate && ad?.endDate) {
      const start = new Date(ad.startDate).getTime();
      const end = new Date(ad.endDate).getTime();
      const now = Date.now();
      if (end > start) {
        progressPercent = Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
        daysRemaining = Math.max(0, Math.round((end - now) / (1000 * 60 * 60 * 24)));
      }
    }

    // Dynamic House Locator for active chart
    const getLordChartRole = (planetName: string) => {
      const pos = chartData.planets?.find(p => p.name === planetName);
      const placedHouse = pos?.house ?? null;
      
      const ZODIAC_LORDS = ['Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter'];
      const ruledHouses: number[] = [];
      for (let h = 1; h <= 12; h++) {
        const sIdx = (ascSignIdx + h - 1) % 12;
        if (ZODIAC_LORDS[sIdx] === planetName) {
          ruledHouses.push(h);
        }
      }

      const isMaraka = marakaDiagnosis.marakaPlanets.includes(planetName);
      const isBadhaka = marakaDiagnosis.badhakesh === planetName;

      return {
        placedHouse,
        ruledHouses,
        isMaraka,
        isBadhaka,
        dignity: pos?.dignity
      };
    };

    const mdRole = md ? getLordChartRole(md.planet) : null;
    const adRole = ad ? getLordChartRole(ad.planet) : null;
    const pdRole = pd ? getLordChartRole(pd.planet) : null;

    // 4-Period Horizon (Previous, Current, Next 2)
    const adList = md?.antardashas || [];
    const currentAdIdx = adList.findIndex(a => a.isCurrent);
    const startIdx = Math.max(0, currentAdIdx - 1);
    const horizonPeriods = adList.slice(startIdx, startIdx + 4);

    return {
      md,
      ad,
      pd,
      progressPercent,
      daysRemaining,
      mdRole,
      adRole,
      pdRole,
      horizonPeriods
    };
  }, [kundali.dashas, chartData, ascSignIdx, marakaDiagnosis]);

  // 3. Situational Sade Sati / Dhaiya Status
  const shaniStatus = useMemo(() => {
    const { sadeSati } = kundali;
    if (!sadeSati) return { isVisible: false, text: '', alertType: 'neutral' };

    if (sadeSati.isInSadeSati) {
      return {
        isVisible: true,
        type: 'sade-sati',
        badge: isHi ? 'साढ़े साती सक्रिय' : 'Sade Sati Active',
        phase: sadeSati.phase,
        saturnSign: getZodiacName(sadeSati.saturnSign, activeLang),
        endDate: sadeSati.activePeriodDetails?.formattedEndDate || '',
        alertType: 'warning' as const
      };
    }

    if (sadeSati.isDhaiyya) {
      return {
        isVisible: true,
        type: 'dhaiyya',
        badge: isHi ? 'ढैय्या सक्रिय' : 'Dhaiyya Active',
        phase: sadeSati.phase,
        saturnSign: getZodiacName(sadeSati.saturnSign, activeLang),
        endDate: sadeSati.activePeriodDetails?.formattedEndDate || '',
        alertType: 'warning' as const
      };
    }

    // If neither active, check upcoming in timeline
    const upcoming = sadeSati.timeline?.find(p => p.status === 'Upcoming');
    if (upcoming && upcoming.startDate) {
      const startMs = new Date(upcoming.startDate).getTime();
      const diffDays = Math.round((startMs - Date.now()) / (1000 * 60 * 60 * 24));
      // Show upcoming alert only if within 18 months (550 days)
      if (diffDays > 0 && diffDays <= 550) {
        const months = Math.round(diffDays / 30);
        return {
          isVisible: true,
          type: 'upcoming',
          badge: isHi ? 'आगामी साढ़ेसाती/ढैय्या' : 'Upcoming Shani Transit',
          phase: upcoming.type,
          saturnSign: getZodiacName(upcoming.saturnSign, activeLang),
          endDate: `${isHi ? 'प्रारंभ:' : 'Begins:'} ${upcoming.formattedStartDate} (${months} ${isHi ? 'माह में' : 'mos'})`,
          alertType: 'info' as const
        };
      }
    }

    // Quiet status (distant > 2 years)
    return {
      isVisible: true,
      type: 'clear',
      badge: isHi ? 'शनि गोचर शांत' : 'Saturn Transit Clear',
      phase: isHi ? 'साढ़ेसाती/ढैय्या प्रभाव मुक्त' : 'No active Sade Sati / Dhaiyya',
      saturnSign: getZodiacName(sadeSati.saturnSign, activeLang),
      endDate: '',
      alertType: 'neutral' as const
    };
  }, [kundali.sadeSati, isHi, activeLang]);

  // Is current Dasha lord a Maraka or Badhaka? (Evaluates both Antardasha and Pratyantardasha)
  const dashaMarakaAlert = useMemo(() => {
    const { adRole, pdRole, mdRole, ad, pd, md } = currentDashaData;
    
    // Check compounded Maraka (Both MD and AD are Marakas)
    if (mdRole?.isMaraka && adRole?.isMaraka) {
      return {
        isAlert: true,
        level: 'warning' as const,
        header: isHi ? `द्वि-स्तरीय मारक संरेखण (MD+AD: ${getPlanetShortCode(md?.planet || '', activeLang)}+${getPlanetShortCode(ad?.planet || '', activeLang)})` : `Dual Maraka Alignment (MD+AD: ${md?.planet} + ${ad?.planet})`,
        text: isHi
          ? `गहन चेतावनी: महादशा (${getPlanetShortCode(md?.planet || '', activeLang)}) और अंतर्दशा (${getPlanetShortCode(ad?.planet || '', activeLang)}) दोनों मारक स्वामी हैं। स्वास्थ्य, सुरक्षा व शांति पर विशेष ध्यान दें।`
          : `High Caution: Both Mahadasha Lord (${md?.planet}) and Antardasha Lord (${ad?.planet}) rule Maraka houses. Acute vitality and wellness care recommended.`
      };
    }

    // Compounded Maraka (Both AD and PD are Marakas)
    if (adRole?.isMaraka && pdRole?.isMaraka) {
      return {
        isAlert: true,
        level: 'warning' as const,
        header: isHi ? 'द्वि-स्तरीय मारक संरेखण (AD+PD)' : 'Compounded Maraka (AD + PD)',
        text: isHi
          ? `गहन चेतावनी: अंतर्दशा (${getPlanetShortCode(ad?.planet || '', activeLang)}) और प्रत्यंतर्दशा (${getPlanetShortCode(pd?.planet || '', activeLang)}) दोनों मारक स्वामी हैं। स्वास्थ्य व शांति पर विशेष ध्यान दें।`
          : `High Caution: Both AD Lord (${ad?.planet}) and PD Lord (${pd?.planet}) rule Maraka houses. Acute vitality care recommended.`
      };
    }

    // Active Mahadasha Maraka Lord
    if (mdRole?.isMaraka) {
      return {
        isAlert: true,
        level: 'warning' as const,
        header: isHi ? `मारकेश महादशा (${getPlanetShortCode(md?.planet || '', activeLang)})` : `Active Maraka Mahadasha (${md?.planet})`,
        text: isHi
          ? `सक्रिय महादशा स्वामी (${getPlanetShortCode(md?.planet || '', activeLang)}) मारक भाव (${mdRole.ruledHouses.map((h: number) => `${h}वें`).join(', ')}) का स्वामी/स्थित है। अंतर्दशा: ${getPlanetShortCode(ad?.planet || '', activeLang)}.`
          : `Active: Current Mahadasha Lord (${md?.planet}) rules Maraka house (${mdRole.ruledHouses.map((h: number) => `H${h}`).join(', ')}). Active Antardasha: ${ad?.planet}. Vitality care advised.`
      };
    }

    // Operational Maraka via Antardasha
    if (adRole?.isMaraka) {
      return {
        isAlert: true,
        level: 'warning' as const,
        header: isHi ? `मारक अंतर्दशा (${getPlanetShortCode(ad?.planet || '', activeLang)})` : `Operational Maraka (AD: ${ad?.planet})`,
        text: isHi
          ? `सक्रिय अंतर्दशा स्वामी (${getPlanetShortCode(ad?.planet || '', activeLang)}) मारक भाव (${adRole.ruledHouses.map((h: number) => `${h}वें`).join(', ')}) का स्वामी है। प्रत्यंतर: ${getPlanetShortCode(pd?.planet || '', activeLang)}.`
          : `Notice: Current AD Lord (${ad?.planet}) rules Maraka house (${adRole.ruledHouses.map((h: number) => `H${h}`).join(', ')}). Active PD: ${pd?.planet}.`
      };
    }

    // Acute sub-trigger via Pratyantardasha
    if (pdRole?.isMaraka) {
      return {
        isAlert: true,
        level: 'warning' as const,
        header: isHi ? `प्रत्यंतर मारक ट्रिगर (${getPlanetShortCode(pd?.planet || '', activeLang)})` : `Acute Maraka Trigger (PD: ${pd?.planet})`,
        text: isHi
          ? `वर्तमान प्रत्यंतर्दशा स्वामी (${getPlanetShortCode(pd?.planet || '', activeLang)}) मारक भाव (${pdRole.ruledHouses.map((h: number) => `${h}वें`).join(', ')}) का स्वामी है। अल्पकालिक सतर्कता रखें।`
          : `Notice: Active PD Lord (${pd?.planet}) rules Maraka house (${pdRole.ruledHouses.map((h: number) => `H${h}`).join(', ')}). Transient sub-period trigger.`
      };
    }

    // Badhakesh via Antardasha
    if (adRole?.isBadhaka) {
      return {
        isAlert: true,
        level: 'info' as const,
        header: isHi ? `बाधक अंतर्दशा (${getPlanetShortCode(ad?.planet || '', activeLang)})` : `Operational Badhaka (AD: ${ad?.planet})`,
        text: isHi
          ? `वर्तमान अंतर्दशा स्वामी (${getPlanetShortCode(ad?.planet || '', activeLang)}) बाधक भाव (${marakaDiagnosis.badhakaHouse}वें) का स्वामी है। कार्यक्षेत्र में अप्रत्याशित बाधाएं संभव हैं।`
          : `Context: AD Lord (${ad?.planet}) rules Badhaka house (${marakaDiagnosis.badhakaHouse}H). Expect purposeful karmic friction/delays.`
      };
    }

    // Badhakesh via Pratyantardasha
    if (pdRole?.isBadhaka) {
      return {
        isAlert: true,
        level: 'info' as const,
        header: isHi ? `प्रत्यंतर बाधक (${getPlanetShortCode(pd?.planet || '', activeLang)})` : `Sub Badhaka (PD: ${pd?.planet})`,
        text: isHi
          ? `वर्तमान प्रत्यंतर्दशा स्वामी (${getPlanetShortCode(pd?.planet || '', activeLang)}) बाधक भाव का स्वामी है।`
          : `Context: PD Lord (${pd?.planet}) rules Badhaka house (${marakaDiagnosis.badhakaHouse}H). Temporary obstacle window.`
      };
    }

    return {
      isAlert: false,
      level: 'neutral' as const,
      header: isHi ? 'मारक स्थिति: शांत / सुरक्षित' : 'Maraka Vulnerability: Nominal',
      text: isHi
        ? `वर्तमान अंतर्दशा (${getPlanetShortCode(ad?.planet || '', activeLang)}) एवं प्रत्यंतर (${getPlanetShortCode(pd?.planet || '', activeLang)}) स्वामी शुभ/तटस्थ हैं।`
        : `Active AD (${ad?.planet}) & PD (${pd?.planet}) are functional benefics. Low Maraka vulnerability.`
    };
  }, [currentDashaData, marakaDiagnosis, isHi, activeLang]);

  return (
    <div ref={containerRef} className="w-full space-y-4">
      {/* Enhanced Unified Vimshottari Cockpit Ribbon */}
      <UnifiedDashaPanel
        kundali={kundali}
        chartData={chartData}
        activeLang={activeLang}
        isHi={isHi}
        selectedPlanet={selectedPlanet}
        onSelectPlanet={onSelectPlanet}
        onSelectHouse={onSelectHouse}
        onOpenDashaDrawer={() => setIsDashaDrawerOpen(true)}
        isNarrow={isNarrow}
        shaniStatus={shaniStatus}
        dashaMarakaAlert={dashaMarakaAlert}
        marakaDiagnosis={marakaDiagnosis}
      />

      {/* 4. Connected House Inspector View */}
      {showFullHouseInspector && (
        <div id="house-inspector-section" className="w-full pt-1">
          <HouseInspectorView
            selectedHouseNumber={selectedHouseNumber}
            onSelectHouse={onSelectHouse}
            selectedPlanet={selectedPlanet}
            onSelectPlanet={onSelectPlanet}
            chartData={chartData}
            kundali={kundali}
            lang={lang}
            layoutMode={layoutMode === 'dock' ? 'all' : 'split-top'}
          />
        </div>
      )}

      {/* Focused Timing Slide-Over Drawer (Down to Prana, Saturn Focus & 5-Yr Horizon) */}
      {isDashaDrawerOpen && (
        <FocusedTimingDrawer
          kundali={kundali}
          chartData={chartData}
          lang={lang}
          onClose={() => setIsDashaDrawerOpen(false)}
          onSelectPlanet={(pName) => {
            const pos = chartData.planets?.find(p => p.name === pName) || null;
            onSelectPlanet(pos);
          }}
          onSelectHouse={onSelectHouse}
        />
      )}
    </div>
  );
};
